import { useCallback } from 'react';
import type { PermissionKey } from '@/lib/permissions';
import { useAuth } from '@/hooks/useAuth';

/**
 * Does the current session hold ALL of these keys?
 *
 * AND semantics, deliberately identical to the server's `requirePermission`.
 * If the two enforcement points disagreed about what a list of keys means, the
 * UI would offer actions the API refuses — so they are the same rule, stated
 * twice, on purpose.
 *
 * That symmetry extends to the empty case. `requirePermission(first, ...rest)`
 * takes a non-empty tuple precisely so an allow-everything guard cannot be
 * written; `keys.every(...)` on an empty array is vacuously TRUE, which is the
 * opposite. The signature below matches the server's, so a caller building the
 * list dynamically — `useHasPermission(...(leaf.requires ? [leaf.requires] : []))`
 * — fails to compile instead of silently rendering everything.
 *
 * Client-side checks decide what to RENDER. They are never the enforcement:
 * every one of these has a matching guard on the route, and the permission set
 * itself comes from the server.
 */
export function useHasPermission(first: PermissionKey, ...rest: PermissionKey[]): boolean {
  const { permissions } = useAuth();
  return [first, ...rest].every((k) => permissions.has(k));
}

/**
 * A stable predicate, for filtering lists — nav groups, home tiles — where
 * calling a hook per item is not an option.
 */
export function useCan(): (key: PermissionKey) => boolean {
  const { permissions } = useAuth();
  return useCallback((key: PermissionKey) => permissions.has(key), [permissions]);
}
